import React from 'react';
import { ArrowLeft, Check, ChevronRight, X } from 'lucide-react';

export type SettingsView = 'root' | 'speed' | 'quality' | 'audio' | 'subtitles' | 'stats';

export interface QualityOption {
  id: string;
  label: string;
}

export interface AudioOption {
  id: string;
  label: string;
}

export interface CaptionOption {
  id: string;
  label: string;
  sub?: string;
}

export interface PlaybackStats {
  resolution?: string;
  durationSec?: number;
  currentSec?: number;
  bufferAheadSec?: number;
  playbackRate?: number;
  volume?: number;
  muted?: boolean;
  droppedFrames?: number;
  totalFrames?: number;
  readyState?: number;
  networkState?: number;
}

interface OdenSettingsMenuProps {
  view: SettingsView;
  onChangeView: (view: SettingsView) => void;
  onClose: () => void;

  // Speed
  speeds: number[];
  currentSpeed: number;
  onSelectSpeed: (rate: number) => void;

  // Quality (optional — row hidden when no options provided)
  qualities?: QualityOption[];
  currentQuality?: string;
  onSelectQuality?: (id: string) => void;

  // Audio (optional)
  audioTracks?: AudioOption[];
  currentAudio?: string;
  onSelectAudio?: (id: string) => void;

  // Subtitles snapshot (root view only — full picker lives in CaptionsMenu)
  captions?: CaptionOption[];
  currentCaption?: string | null;
  onOpenCaptions?: () => void;

  // Stats
  stats?: PlaybackStats;

  // Shortcuts
  onOpenShortcuts: () => void;
}

function speedLabel(rate: number): string {
  return rate === 1 ? 'Normal' : `${rate}×`;
}

function shortStatLabel(value: number | undefined, suffix = ''): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  return value.toFixed(suffix === '×' ? 2 : 0) + suffix;
}

const OdenSettingsMenu: React.FC<OdenSettingsMenuProps> = ({
  view,
  onChangeView,
  onClose,
  speeds,
  currentSpeed,
  onSelectSpeed,
  qualities,
  currentQuality,
  onSelectQuality,
  audioTracks,
  currentAudio,
  onSelectAudio,
  captions,
  currentCaption,
  onOpenCaptions,
  stats,
  onOpenShortcuts,
}) => {
  if (view === 'speed') {
    return (
      <div className="oden-menu" role="menu">
        <div className="oden-menu-hd">
          <button type="button" className="oden-menu-back" onClick={() => onChangeView('root')} aria-label="Back">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <ArrowLeft size={14} />
            </span>
          </button>
          <span>Playback speed</span>
          <button type="button" className="oden-menu-x" onClick={onClose} aria-label="Close">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <X size={14} />
            </span>
          </button>
        </div>
        {speeds.map((rate) => {
          const active = Math.abs(rate - currentSpeed) < 0.001;
          return (
            <button
              key={rate}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className="oden-menu-row"
              data-on={active ? '1' : '0'}
              onClick={() => onSelectSpeed(rate)}
            >
              <span style={{ width: 16, height: 16, display: 'inline-flex', opacity: active ? 1 : 0 }}>
                <Check size={16} />
              </span>
              <span style={{ flex: 1 }}>{speedLabel(rate)}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (view === 'quality' && qualities && qualities.length > 0) {
    return (
      <div className="oden-menu" role="menu">
        <div className="oden-menu-hd">
          <button type="button" className="oden-menu-back" onClick={() => onChangeView('root')} aria-label="Back">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <ArrowLeft size={14} />
            </span>
          </button>
          <span>Quality</span>
          <button type="button" className="oden-menu-x" onClick={onClose} aria-label="Close">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <X size={14} />
            </span>
          </button>
        </div>
        {qualities.map((q) => {
          const active = q.id === currentQuality;
          return (
            <button
              key={q.id}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className="oden-menu-row"
              data-on={active ? '1' : '0'}
              onClick={() => onSelectQuality?.(q.id)}
            >
              <span style={{ width: 16, height: 16, display: 'inline-flex', opacity: active ? 1 : 0 }}>
                <Check size={16} />
              </span>
              <span style={{ flex: 1 }}>{q.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (view === 'audio' && audioTracks && audioTracks.length > 0) {
    return (
      <div className="oden-menu" role="menu">
        <div className="oden-menu-hd">
          <button type="button" className="oden-menu-back" onClick={() => onChangeView('root')} aria-label="Back">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <ArrowLeft size={14} />
            </span>
          </button>
          <span>Audio track</span>
          <button type="button" className="oden-menu-x" onClick={onClose} aria-label="Close">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <X size={14} />
            </span>
          </button>
        </div>
        {audioTracks.map((a) => {
          const active = a.id === currentAudio;
          return (
            <button
              key={a.id}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className="oden-menu-row"
              data-on={active ? '1' : '0'}
              onClick={() => onSelectAudio?.(a.id)}
            >
              <span style={{ width: 16, height: 16, display: 'inline-flex', opacity: active ? 1 : 0 }}>
                <Check size={16} />
              </span>
              <span style={{ flex: 1 }}>{a.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (view === 'stats') {
    const bufferAhead = (stats?.bufferAheadSec ?? 0).toFixed(1);
    const droppedRatio =
      stats?.totalFrames && stats.totalFrames > 0 && stats.droppedFrames !== undefined
        ? `${stats.droppedFrames} / ${stats.totalFrames}`
        : stats?.droppedFrames !== undefined
        ? String(stats.droppedFrames)
        : '—';
    return (
      <div className="oden-menu" role="menu">
        <div className="oden-menu-hd">
          <button type="button" className="oden-menu-back" onClick={() => onChangeView('root')} aria-label="Back">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <ArrowLeft size={14} />
            </span>
          </button>
          <span>Stats for nerds</span>
          <button type="button" className="oden-menu-x" onClick={onClose} aria-label="Close">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <X size={14} />
            </span>
          </button>
        </div>
        <div className="oden-menu-row" style={{ cursor: 'default' }}>
          <span style={{ flex: 1 }}>Resolution</span>
          <span className="oden-menu-val">{stats?.resolution ?? '—'}</span>
        </div>
        <div className="oden-menu-row" style={{ cursor: 'default' }}>
          <span style={{ flex: 1 }}>Buffer ahead</span>
          <span className="oden-menu-val">{bufferAhead}s</span>
        </div>
        <div className="oden-menu-row" style={{ cursor: 'default' }}>
          <span style={{ flex: 1 }}>Playback rate</span>
          <span className="oden-menu-val">{shortStatLabel(stats?.playbackRate, '×')}</span>
        </div>
        <div className="oden-menu-row" style={{ cursor: 'default' }}>
          <span style={{ flex: 1 }}>Volume</span>
          <span className="oden-menu-val">
            {stats?.muted ? 'muted' : stats?.volume !== undefined ? `${Math.round(stats.volume * 100)}%` : '—'}
          </span>
        </div>
        <div className="oden-menu-row" style={{ cursor: 'default' }}>
          <span style={{ flex: 1 }}>Frames dropped</span>
          <span className="oden-menu-val">{droppedRatio}</span>
        </div>
        <div className="oden-menu-row" style={{ cursor: 'default' }}>
          <span style={{ flex: 1 }}>Ready state</span>
          <span className="oden-menu-val">{stats?.readyState ?? '—'}</span>
        </div>
      </div>
    );
  }

  // ── Root view ───────────────────────────────────────────────────────────
  const speedRowLabel = speedLabel(currentSpeed);
  const qualityRowLabel = qualities && currentQuality ? qualities.find((q) => q.id === currentQuality)?.label : null;
  const audioRowLabel = audioTracks && currentAudio ? audioTracks.find((a) => a.id === currentAudio)?.label : null;
  const captionRowLabel =
    captions && currentCaption ? captions.find((c) => c.id === currentCaption)?.label : 'Off';

  return (
    <div className="oden-menu" role="menu">
      <div className="oden-menu-hd">
        <span style={{ flex: 1 }}>Settings</span>
        <button type="button" className="oden-menu-x" onClick={onClose} aria-label="Close">
          <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
            <X size={14} />
          </span>
        </button>
      </div>

      <button type="button" className="oden-menu-row" onClick={() => onChangeView('speed')} role="menuitem">
        <span style={{ flex: 1 }}>Playback speed</span>
        <span className="oden-menu-val">{speedRowLabel}</span>
        <span style={{ width: 14, height: 14, display: 'inline-flex', opacity: 0.5 }}>
          <ChevronRight size={14} />
        </span>
      </button>

      {qualities && qualities.length > 0 && (
        <button type="button" className="oden-menu-row" onClick={() => onChangeView('quality')} role="menuitem">
          <span style={{ flex: 1 }}>Quality</span>
          <span className="oden-menu-val">{qualityRowLabel ?? 'Auto'}</span>
          <span style={{ width: 14, height: 14, display: 'inline-flex', opacity: 0.5 }}>
            <ChevronRight size={14} />
          </span>
        </button>
      )}

      {audioTracks && audioTracks.length > 1 && (
        <button type="button" className="oden-menu-row" onClick={() => onChangeView('audio')} role="menuitem">
          <span style={{ flex: 1 }}>Audio track</span>
          <span className="oden-menu-val">{audioRowLabel ?? 'Default'}</span>
          <span style={{ width: 14, height: 14, display: 'inline-flex', opacity: 0.5 }}>
            <ChevronRight size={14} />
          </span>
        </button>
      )}

      {captions && captions.length > 0 && onOpenCaptions && (
        <button type="button" className="oden-menu-row" onClick={onOpenCaptions} role="menuitem">
          <span style={{ flex: 1 }}>Subtitles</span>
          <span className="oden-menu-val">{captionRowLabel}</span>
          <span style={{ width: 14, height: 14, display: 'inline-flex', opacity: 0.5 }}>
            <ChevronRight size={14} />
          </span>
        </button>
      )}

      <div className="oden-menu-sep" />

      <button type="button" className="oden-menu-row" onClick={() => onChangeView('stats')} role="menuitem">
        <span style={{ flex: 1 }}>Stats for nerds</span>
        <span style={{ width: 14, height: 14, display: 'inline-flex', opacity: 0.5 }}>
          <ChevronRight size={14} />
        </span>
      </button>

      <button type="button" className="oden-menu-row" onClick={onOpenShortcuts} role="menuitem">
        <span style={{ flex: 1 }}>Keyboard shortcuts</span>
        <span className="oden-menu-kbd">?</span>
      </button>
    </div>
  );
};

export default OdenSettingsMenu;
